import { supabase } from '@/lib/supabase';

/**
 * Client-side behavior tracking → POST /api/activity → `user_activity_log`.
 * This is Ezra's (AI Behavioral Insights Lead) raw material: page views,
 * clicks, and search submissions across every visitor, tied together by a
 * session id so cross-user paths and funnels can be mined server-side
 * (server/ezra-insights.js).
 *
 * Design rules, in order:
 *   1. NEVER affect the product. Every call is fire-and-forget, catches
 *      everything, and uses keepalive so navigation doesn't cancel sends.
 *   2. Reuse the BOOKING session id when one exists. BookingFlowContext
 *      keeps `defera_booking_session` in localStorage and stamps its
 *      sessionId onto abandoned_trips rows — reading the same id here lets
 *      Ezra join clickstream sessions against abandoned carts. Visitors who
 *      never opened the booking flow get a session-scoped fallback id.
 *   3. Bounded volume. Consecutive duplicate page_views are deduped and
 *      click events are capped per session so a rage-clicker or an idle
 *      tab can't flood the log.
 */

const FALLBACK_SESSION_KEY = 'defera_session_id';
const BOOKING_SESSION_KEY = 'defera_booking_session';
const MAX_CLICKS_PER_SESSION = 150;
const MAX_LABEL_CHARS = 48;

let clickCount = 0;
let lastPageViewPath = null;

/** Booking-flow session id when present (joins to abandoned_trips), else a
 *  seeded per-tab-session id in the format the cart flow uses. */
export function getTrackingSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage?.getItem(BOOKING_SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.sessionId) return parsed.sessionId;
    }
  } catch { /* fall through to the fallback id */ }
  try {
    let id = window.sessionStorage?.getItem(FALLBACK_SESSION_KEY);
    if (!id) {
      id = `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      window.sessionStorage?.setItem(FALLBACK_SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/** Fire-and-forget event send. Never throws, never blocks. */
export function trackEvent(activityType, activityData = {}) {
  if (!activityType || typeof window === 'undefined') return;
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      await fetch('/api/activity', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          activity_type: activityType,
          activity_data: activityData || {},
          page_url: window.location?.pathname || null,
          session_id: getTrackingSessionId(),
        }),
        keepalive: true,
      });
    } catch { /* telemetry must never surface */ }
  })();
}

/** Route-change page view, deduping consecutive repeats of the same path. */
export function trackPageView(path) {
  if (!path || path === lastPageViewPath) return;
  lastPageViewPath = path;
  trackEvent('page_view', { path });
}

/**
 * Delegated click capture on buttons/links. Only elements with a readable
 * label are logged (an unlabeled div click carries no analytical meaning),
 * capped per session. Returns an uninstaller for React effect cleanup.
 */
export function installClickTracking() {
  if (typeof document === 'undefined') return () => {};
  const onClick = (e) => {
    try {
      if (clickCount >= MAX_CLICKS_PER_SESSION) return;
      const el = e.target?.closest?.('button, a, [role="button"]');
      if (!el) return;
      const label = (el.getAttribute('aria-label') || el.textContent || '')
        .trim().replace(/\s+/g, ' ').slice(0, MAX_LABEL_CHARS);
      if (!label) return;
      clickCount += 1;
      trackEvent('click', {
        label,
        tag: el.tagName.toLowerCase(),
        path: window.location?.pathname || null,
      });
    } catch { /* telemetry must never surface */ }
  };
  document.addEventListener('click', onClick, { capture: true, passive: true });
  return () => document.removeEventListener('click', onClick, { capture: true });
}
